import songService from '../services/songService';
import { getSetting, setSetting, settingKeys } from '../utils/settings';
import * as Sentry from '@sentry/browser';

// Cooldown period in milliseconds (1 hour)
const PLAY_COUNT_COOLDOWN = 60 * 60 * 1000; // 1 hour

// Maximum age for cooldown entries (24 hours)
const MAX_COOLDOWN_AGE = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Checks if enough time has passed since the last play count for a song
 * @param {string} songId - The ID of the song
 * @returns {boolean} - True if cooldown has passed, false otherwise
 */
const canIncrementPlayCount = (songId) => {
  const cooldowns = getSetting(settingKeys.songPlayCooldowns) || {};
  const lastPlayTime = cooldowns[songId];

  if (!lastPlayTime) {
    return true; // First time playing this song
  }

  const now = Date.now();
  const timeSinceLastPlay = now - lastPlayTime;

  return timeSinceLastPlay >= PLAY_COUNT_COOLDOWN;
};

/**
 * Records the current timestamp as the last play time for a song
 * @param {string} songId - The ID of the song
 */
const recordPlayTime = (songId) => {
  const cooldowns = getSetting(settingKeys.songPlayCooldowns) || {};
  cooldowns[songId] = Date.now();
  setSetting(settingKeys.songPlayCooldowns, cooldowns);
};

/**
 * Increments the play count for a song with cooldown protection
 * @param {string} songId - The ID of the song
 * @param {boolean} skipIncrement - If true, skips the increment (for editing scenarios)
 * @returns {Promise<void>}
 */
export const incrementSongPlayCount = async (songId, skipIncrement = false) => {
  // Skip incrementing if this is from an edit save
  if (skipIncrement) {
    return;
  }

  // Check if cooldown period has passed
  if (!canIncrementPlayCount(songId)) {
    return;
  }

  try {
    await songService.incrementSongPlayCount(songId);

    // Record the play time to prevent rapid increments
    recordPlayTime(songId);
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        section: 'song-stats',
        action: 'increment-play-count',
      },
      extra: {
        songId,
      },
    });
  }
};

/**
 * Gets the remaining cooldown time for a song in minutes
 * @param {string} songId - The ID of the song
 * @returns {number} - Remaining cooldown time in minutes, or 0 if no cooldown
 */
export const getRemainingCooldown = (songId) => {
  const cooldowns = getSetting(settingKeys.songPlayCooldowns) || {};
  const lastPlayTime = cooldowns[songId];

  if (!lastPlayTime) {
    return 0; // No cooldown
  }

  const now = Date.now();
  const timeSinceLastPlay = now - lastPlayTime;
  const remainingCooldown = PLAY_COUNT_COOLDOWN - timeSinceLastPlay;

  return remainingCooldown > 0 ? Math.ceil(remainingCooldown / (60 * 1000)) : 0;
};

/**
 * Clears old cooldown entries (older than 24 hours) to prevent localStorage bloat
 */
export const cleanupOldCooldowns = () => {
  const cooldowns = getSetting(settingKeys.songPlayCooldowns) || {};
  const now = Date.now();
  const maxAge = MAX_COOLDOWN_AGE;

  const cleanedCooldowns = {};

  Object.entries(cooldowns).forEach(([songId, timestamp]) => {
    if (now - timestamp < maxAge) {
      cleanedCooldowns[songId] = timestamp;
    }
  });

  setSetting(settingKeys.songPlayCooldowns, cleanedCooldowns);
};

/**
 * Sorts songs by popularity (timesPlayed)
 * @param {Array} songs - Array of song objects
 * @param {string} sortType - 'most' for descending, 'least' for ascending
 * @returns {Array} Sorted array of songs
 */
export const sortSongsByPopularity = (songs, sortType) => {
  return [...songs].sort((a, b) => {
    const aPlays = a.stats?.timesPlayed || 0;
    const bPlays = b.stats?.timesPlayed || 0;

    if (sortType === 'most') {
      // Descending: highest plays first
      if (bPlays !== aPlays) return bPlays - aPlays;
    } else {
      // Ascending: lowest plays first
      if (aPlays !== bPlays) return aPlays - bPlays;
    }

    // Secondary sort by title for consistent ordering
    return a.title.localeCompare(b.title);
  });
};

/**
 * Clears all cooldowns (useful for testing)
 */
export const clearAllCooldowns = () => {
  setSetting(settingKeys.songPlayCooldowns, {});
};

/**
 * Finds the top N most popular songs in a collection
 * Includes all songs tied at the Nth position
 * @param {Array} songs - Array of songs to analyze
 * @param {number} topN - Number of top songs to identify (default: 10)
 * @returns {Object} Object containing maxPlays, popularSongs array, threshold, and mostPlayedOnly
 */
export const getPopularSongs = (songs, topN = 10) => {
  if (songs.length === 0) return { maxPlays: 0, popularSongs: [], threshold: 0, mostPlayedOnly: false };

  // Sort by plays descending
  const sorted = [...songs].sort((a, b) => (b.stats?.timesPlayed || 0) - (a.stats?.timesPlayed || 0));

  const maxPlays = sorted[0]?.stats?.timesPlayed || 0;

  // A top ten of 20 songs or fewer marks half the library, so a small one gets just its single
  // most played song, and nothing at all when the top spot is tied
  if (songs.length <= topN * 2) {
    const secondPlays = sorted[1]?.stats?.timesPlayed || 0;
    const popularSongs = maxPlays > 0 && maxPlays > secondPlays ? [sorted[0]] : [];
    return { maxPlays, popularSongs, threshold: maxPlays, mostPlayedOnly: true };
  }

  // Get the play count threshold from the Nth song (0-indexed, so topN-1)
  const threshold = sorted[topN - 1]?.stats?.timesPlayed || 0;

  // Include ALL songs at or above threshold (handles ties)
  // But exclude songs with 0 plays
  const popularSongs = sorted.filter((song) => {
    const plays = song.stats?.timesPlayed || 0;
    return plays >= threshold && plays > 0;
  });

  return { maxPlays, popularSongs, threshold, mostPlayedOnly: false };
};

const songStatsHelper = {
  incrementSongPlayCount,
  sortSongsByPopularity,
};

export default songStatsHelper;
